import { useEffect, useMemo, useRef, useState } from "react";
import { api, money, dateLong, timeShort } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sparkles, RefreshCw, Calendar as CalendarIcon, Sunrise, Moon,
  AlertCircle, DollarSign, Newspaper, Bell, CheckCircle2, Globe,
  LayoutGrid, GripVertical, EyeOff, Eye, RotateCcw, Check, Timer,
  Plus, StretchHorizontal,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import GettingStarted from "@/components/GettingStarted";
import FocusBar from "@/components/FocusBar";

const DEFAULT_ORDER = ["ai_brief", "focus", "money_watch", "schedule", "tomorrow", "priority", "followups", "meetings", "news", "deadlines"];

export default function Today() {
  const { user } = useAuth();
  const { settings, update } = useSettings() || {};
  const [data, setData] = useState(null);
  const [busyStart, setBusyStart] = useState(false);
  const [busyReschedule, setBusyReschedule] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [order, setOrder] = useState(DEFAULT_ORDER);
  const [hidden, setHidden] = useState([]);
  const [sizes, setSizes] = useState({});
  const [resizing, setResizing] = useState(null);
  const [confirming, setConfirming] = useState(null); // "start" | "end"
  const [dragKey, setDragKey] = useState(null);
  const dragKeyRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/today");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  // Hydrate layout from saved settings
  useEffect(() => {
    const layout = settings?.today_layout;
    // Legacy layouts (pre-resize) get the new default arrangement once.
    const legacy = layout && !layout.sizes;
    const savedOrder = !legacy && Array.isArray(layout?.order)
      ? layout.order.filter((k) => DEFAULT_ORDER.includes(k))
      : [];
    const merged = [...savedOrder];
    DEFAULT_ORDER.forEach((k, i) => {
      if (!merged.includes(k)) merged.splice(Math.min(i, merged.length), 0, k);
    });
    setOrder(merged);
    setHidden(Array.isArray(layout?.hidden) ? layout.hidden.filter((k) => DEFAULT_ORDER.includes(k)) : []);
    setSizes(layout?.sizes && typeof layout.sizes === "object" ? layout.sizes : {});
  }, [settings?.today_layout]);

  const persist = async (nextOrder, nextHidden, nextSizes = sizes) => {
    try { await update?.({ today_layout: { order: nextOrder, hidden: nextHidden, sizes: nextSizes } }); }
    catch { toast.error("Could not save layout"); }
  };

  const setWidgetSpan = (key, span) => {
    const next = { ...sizes, [key]: span };
    setSizes(next);
    persist(order, hidden, next);
  };

  const startDay = async () => {
    setBusyStart(true);
    try {
      await api.post("/today/start-day");
      toast.success("Morning brief ready");
      await load();
    } catch (e) { toast.error("Could not generate brief"); }
    finally { setBusyStart(false); }
  };

  const reschedule = async () => {
    setBusyReschedule(true);
    try {
      const { data: r } = await api.post("/today/reschedule");
      toast.success(`Scheduled ${r.today} today · ${r.tomorrow} tomorrow${r.later ? ` · ${r.later} later` : ""}${r.unscheduled ? ` · ${r.unscheduled} didn't fit` : ""}`);
      await load();
    } catch { toast.error("Reschedule failed"); }
    finally { setBusyReschedule(false); }
  };

  const endOfDay = async () => {
    try {
      const { data: r } = await api.post("/today/end-of-day");
      toast.success(`Wrap: ${r.completed} completed, ${r.carryover} carry over`);
    } catch { toast.error("Could not wrap day"); }
  };

  const hideWidget = (key) => {
    const nextHidden = [...hidden, key];
    setHidden(nextHidden);
    persist(order, nextHidden);
  };
  const showWidget = (key) => {
    const nextHidden = hidden.filter((k) => k !== key);
    setHidden(nextHidden);
    persist(order, nextHidden);
  };
  const resetLayout = () => {
    setOrder(DEFAULT_ORDER);
    setHidden([]);
    setSizes({});
    persist(DEFAULT_ORDER, [], {});
    toast.success("Layout reset");
  };

  const addQuickTask = async (title) => {
    const start = new Date();
    start.setMinutes(start.getMinutes() + (15 - (start.getMinutes() % 15)), 0, 0);
    await api.post("/tasks", {
      title,
      duration_minutes: 30,
      scheduled_for: start.toISOString(),
      priority: "normal",
    });
    toast.success("Added to today");
    await load();
  };

  const startDragWidget = (key) => { dragKeyRef.current = key; setDragKey(key); };
  const onDragOverWidget = (key) => {
    const moving = dragKeyRef.current;
    if (!moving || moving === key) return;
    setOrder((prev) => {
      const next = prev.filter((k) => k !== moving);
      const idx = next.indexOf(key);
      next.splice(idx < 0 ? next.length : idx, 0, moving);
      return next;
    });
  };
  const onDragEndWidget = () => {
    dragKeyRef.current = null;
    setDragKey(null);
    persist(order, hidden);
  };

  const visible = useMemo(() => order.filter((k) => !hidden.includes(k)), [order, hidden]);

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-72" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["a", "b", "c", "d"].map((k) => <Skeleton key={k} className="h-28" />)}
        </div>
      </div>
    );
  }

  const currency = data.metrics.currency;
  const greeting = greetFor(new Date());
  const phase = phaseFor(new Date());
  const ctx = { data, settings, currency, reload: load, addQuickTask };

  return (
    <div data-testid="today-page" className="space-y-8">
      {/* Hero */}
      <section className={cn("studio-daylight rounded-2xl -m-2 p-2")} data-phase={phase} data-testid="today-hero">
        <div className="p-4 md:p-6 lg:p-8 rounded-xl relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{PHASE_LABEL[phase]}</div>
              <h1 className="mt-1 text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight">
                {greeting}, {user?.name?.split(" ")[0] || "there"}.
              </h1>
              <p className="mt-2 text-muted-foreground text-sm md:text-base">{dateLong(data.date)}</p>
            </div>
            <div className="flex flex-wrap gap-2 -mx-1 px-1 overflow-x-auto md:overflow-visible">
              <Button
                data-testid="customise-layout-btn"
                variant={customising ? "default" : "outline"}
                onClick={() => { if (customising) persist(order, hidden); setCustomising((v) => !v); }}
                className="whitespace-nowrap"
              >
                {customising ? <Check className="w-4 h-4 mr-2" /> : <LayoutGrid className="w-4 h-4 mr-2" />}
                {customising ? "Done" : "Customise"}
              </Button>
              {customising && (
                <Button data-testid="reset-layout-btn" variant="outline" onClick={resetLayout} className="whitespace-nowrap">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reset layout
                </Button>
              )}
              <Button data-testid="reschedule-day-btn" variant="outline" onClick={reschedule} disabled={busyReschedule} className="whitespace-nowrap">
                <RefreshCw className="w-4 h-4 mr-2" /> {busyReschedule ? "Rescheduling…" : "Reschedule Day"}
              </Button>
              <Button data-testid="end-of-day-btn" variant="outline" onClick={() => setConfirming("end")} className="whitespace-nowrap">
                <Moon className="w-4 h-4 mr-2" /> End of Day
              </Button>
              <Button
                data-testid="start-my-day-btn"
                onClick={() => setConfirming("start")}
                disabled={busyStart}
                className="h-11 px-5 whitespace-nowrap bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))] text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {busyStart ? "Planning…" : "START MY DAY"}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {customising
              ? "Drag widgets by the handle to reorder, or hide the ones you don't need."
              : "AI will plan and prioritise your day."}
          </p>
        </div>
      </section>

      {/* Getting started checklist */}
      <GettingStarted />

      {/* Hidden widgets tray */}
      {customising && hidden.length > 0 && (
        <section className="rounded-xl border border-dashed border-border p-4" data-testid="hidden-widgets-tray">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">Hidden widgets</div>
          <div className="flex flex-wrap gap-2">
            {hidden.map((k) => {
              const def = WIDGET_DEFS[k];
              if (!def) return null;
              return (
                <button
                  key={k}
                  onClick={() => showWidget(k)}
                  data-testid={`show-widget-${k}`}
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" /> {def.title}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Widget grid */}
      <section className="grid grid-cols-1 lg:grid-cols-6 gap-6 items-start" data-testid="today-widget-grid">
        {visible.map((key) => {
          const def = WIDGET_DEFS[key];
          if (!def) return null;
          return (
            <WidgetShell
              key={key}
              widgetKey={key}
              def={def}
              customising={customising}
              isDragging={dragKey === key}
              onDragStart={() => startDragWidget(key)}
              onDragOver={() => onDragOverWidget(key)}
              onDragEnd={onDragEndWidget}
              onHide={() => hideWidget(key)}
              span={sizes[key] || def.span}
              onResize={(span) => setWidgetSpan(key, span)}
              resizing={resizing === key}
              setResizing={(v) => setResizing(v ? key : null)}
            >
              {def.render(ctx)}
            </WidgetShell>
          );
        })}
      </section>

      {visible.length === 0 && (
        <Card className="studio-card p-10 text-center text-sm text-muted-foreground" data-testid="today-empty-layout">
          Every widget is hidden. Press <b className="mx-1">Customise</b> to bring some back.
        </Card>
      )}

      <AlertDialog open={!!confirming} onOpenChange={(o) => { if (!o) setConfirming(null); }}>
        <AlertDialogContent data-testid="today-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === "start" ? "Start your day?" : "Wrap up the day?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === "start"
                ? "Mahano will read your projects, tasks, money and industry news, then write today's brief with suggestions. This replaces the current brief."
                : "Mahano will tally what you completed today and carry unfinished tasks over. Nothing is deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="today-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="today-confirm-ok"
              onClick={() => {
                const which = confirming;
                setConfirming(null);
                if (which === "start") startDay(); else endOfDay();
              }}
            >
              {confirming === "start" ? "Yes, plan my day" : "Yes, wrap the day"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const SPAN_CLASS = { 2: "lg:col-span-2", 3: "lg:col-span-3", 4: "lg:col-span-4", 6: "lg:col-span-6" };
const SPAN_STEPS = [2, 3, 4, 6];

function WidgetShell({
  widgetKey, def, customising, isDragging, onDragStart, onDragOver, onDragEnd, onHide,
  span, onResize, resizing, setResizing, children,
}) {
  const Icon = def.icon;
  const ref = useRef(null);

  // Drag the right edge to snap the card between 1/3, 1/2, 2/3 and full width
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    const grid = ref.current?.parentElement;
    const gridWidth = grid?.getBoundingClientRect().width || 1200;
    const colWidth = gridWidth / 6;
    const startX = e.clientX;
    const startSpan = span;
    let latest = startSpan;
    const onMove = (ev) => {
      const deltaCols = Math.round((ev.clientX - startX) / colWidth);
      const target = Math.min(6, Math.max(2, startSpan + deltaCols));
      const snapped = SPAN_STEPS.reduce((a, b) => (Math.abs(b - target) < Math.abs(a - target) ? b : a), 2);
      if (snapped !== latest) { latest = snapped; onResize(snapped); }
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const cycleSpan = () => {
    const i = SPAN_STEPS.indexOf(span);
    onResize(SPAN_STEPS[(i + 1) % SPAN_STEPS.length]);
  };

  return (
    <div
      ref={ref}
      className={cn(
        SPAN_CLASS[span] || "lg:col-span-2",
        customising && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
        resizing && "select-none"
      )}
      draggable={customising && !resizing}
      onDragStart={onDragStart}
      onDragOver={(e) => { if (customising) { e.preventDefault(); onDragOver(); } }}
      onDragEnd={onDragEnd}
      data-testid={`widget-${widgetKey}`}
      data-span={span}
    >
      <Card
        className={cn(
          "studio-card relative h-[360px] flex flex-col overflow-hidden",
          def.headerClass,
          customising && "ring-1 ring-dashed ring-[hsl(var(--studio-primary)/0.5)]"
        )}
        data-testid={def.testid}
      >
        <CardHeader className="pb-2 shrink-0">
          <CardTitle className="flex items-center gap-2 text-base">
            {customising && <GripVertical className="w-4 h-4 text-muted-foreground/60" data-testid={`widget-handle-${widgetKey}`} />}
            <Icon className={cn("w-4 h-4", def.iconClass)} />
            {def.title}
            <div className="ml-auto flex items-center gap-1">
              {customising && (
                <>
                  <button
                    onClick={cycleSpan}
                    data-testid={`resize-widget-${widgetKey}`}
                    className="text-muted-foreground hover:text-foreground"
                    title="Cycle width"
                  >
                    <StretchHorizontal className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onHide}
                    data-testid={`hide-widget-${widgetKey}`}
                    className="text-muted-foreground hover:text-foreground"
                    title="Hide widget"
                  >
                    <EyeOff className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>

        <CardContent className={cn("flex-1 min-h-0 relative", def.more ? "pb-12" : "pb-4")} data-testid={`widget-body-${widgetKey}`}>
          <div className={cn("h-full", def.scroll ? "overflow-y-auto pr-1" : "overflow-hidden")}>{children}</div>
          {def.more && <div className="pointer-events-none absolute left-0 right-0 bottom-10 h-8 bg-gradient-to-t from-card to-transparent" />}
        </CardContent>

        {def.more && (
          <Link
            to={def.more}
            data-testid={`widget-more-${widgetKey}`}
            className="absolute bottom-0 left-0 right-0 px-6 py-3 text-[11px] uppercase tracking-widest font-medium text-[hsl(var(--studio-primary))] hover:bg-accent/60 border-t border-border transition-colors"
          >
            + More
          </Link>
        )}

        {customising && (
          <div
            onMouseDown={startResize}
            data-testid={`resize-handle-${widgetKey}`}
            className="absolute top-0 right-0 h-full w-2 cursor-col-resize bg-[hsl(var(--studio-primary)/0.25)] hover:bg-[hsl(var(--studio-primary)/0.5)]"
            title="Drag to resize"
          />
        )}
      </Card>
    </div>
  );
}

const WIDGET_DEFS = {
  focus: {
    title: "Focus",
    icon: Timer,
    iconClass: "text-[hsl(var(--studio-primary))]",
    span: 2,
    scroll: true,
    testid: "focus-card",
    render: ({ data, reload }) => <FocusBar data={data} onChanged={reload} />,
  },

  ai_brief: {
    title: "Morning Brief",
    icon: Sunrise,
    iconClass: "text-[hsl(30_92%_55%)]",
    headerClass: "studio-sunrise",
    span: 4,
    scroll: true,
    testid: "ai-brief-card",
    render: ({ data, settings, addQuickTask }) => <MorningBrief data={data} settings={settings} addQuickTask={addQuickTask} />,
  },

  money_watch: {
    title: "Money Watch",
    icon: DollarSign,
    span: 2,
    more: "/finance",
    testid: "money-watch-card",
    render: ({ data, currency }) => (
      <div className="space-y-3">
        <Row label="Outstanding" value={money(data.money_watch.outstanding, currency)} />
        <Row label="Overdue" value={money(data.money_watch.overdue_amount, currency)} tone={data.money_watch.overdue_amount > 0 ? "danger" : "default"} />
        <Row label="Paid this month" value={money(data.money_watch.paid_this_month, currency)} tone="success" />
        <Link to="/finance" className="block pt-2 text-xs text-[hsl(var(--studio-primary))] hover:underline">Open Finance →</Link>
      </div>
    ),
  },

  schedule: {
    title: "Today's Schedule",
    icon: CalendarIcon,
    span: 2,
    more: "/calendar",
    testid: "schedule-card",
    render: ({ data }) => (
      <DayPlan
        events={data.schedule || []}
        tasks={data.tasks_today || []}
        emptyMsg="Nothing scheduled today. Press Reschedule Day to fill your slots."
        testid="today-plan"
      />
    ),
  },

  tomorrow: {
    title: "Tomorrow's Plan",
    icon: Sunrise,
    span: 2,
    more: "/calendar",
    testid: "tomorrow-card",
    render: ({ data }) => (
      <DayPlan
        events={data.schedule_tomorrow || []}
        tasks={data.tasks_tomorrow || []}
        emptyMsg="Tomorrow is wide open. Reschedule Day will fill it."
        testid="tomorrow-plan"
      />
    ),
  },

  priority: {
    title: "Priority Stack",
    icon: AlertCircle,
    span: 2,
    more: "/tasks",
    testid: "priority-card",
    render: ({ data }) => (
      <div className="space-y-2">
        {data.priority_stack.length === 0 ? (
          <EmptyState msg="No urgent items. Focus on what moves things forward." />
        ) : data.priority_stack.map((t) => (
          <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <PriorityBadge priority={t.priority} />
              <div>
                <div className="text-sm font-medium">{t.title}</div>
                {t.due_date && <div className="text-xs text-muted-foreground">Due {t.due_date}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>
    ),
  },

  followups: {
    title: "Follow-ups Due",
    icon: Bell,
    span: 2,
    more: "/contacts",
    testid: "followups-card",
    render: ({ data }) => (
      <div className="space-y-2">
        {data.follow_ups.length === 0 ? <EmptyState msg="Inbox zero on your follow-ups." /> :
          data.follow_ups.map((f) => (
            <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="text-sm">{f.title}</div>
              <Badge variant="secondary">{f.due_date}</Badge>
            </div>
          ))
        }
      </div>
    ),
  },

  meetings: {
    title: "Upcoming Meetings",
    icon: CalendarIcon,
    span: 2,
    more: "/calendar",
    testid: "meetings-card",
    render: ({ data }) => (
      <div className="space-y-2">
        {data.meetings.length === 0 ? <EmptyState msg="No meetings on the horizon." /> :
          data.meetings.map((m) => (
            <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <div className="text-sm font-medium">{m.title}</div>
                <div className="text-xs text-muted-foreground">{new Date(m.start).toLocaleString()}</div>
              </div>
            </div>
          ))
        }
      </div>
    ),
  },

  news: {
    title: "Industry News",
    icon: Newspaper,
    span: 2,
    more: "/news",
    testid: "news-card",
    render: ({ data }) => (
      <div className="space-y-2">
        {data.news.length === 0 ? <EmptyState msg="Refresh news from the News page." /> :
          data.news.map((n) => (
            <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="block p-3 rounded-lg border border-border hover:bg-accent">
              <div className="text-sm font-medium">{n.headline}</div>
              <div className="text-xs text-muted-foreground">{n.source}</div>
            </a>
          ))
        }
        <Link to="/news" className="block pt-2 text-xs text-[hsl(var(--studio-primary))] hover:underline">Open News →</Link>
      </div>
    ),
  },

  deadlines: {
    title: "Upcoming Deadlines",
    icon: CheckCircle2,
    span: 2,
    more: "/projects",
    testid: "deadlines-card",
    render: ({ data }) => (
      <div className="space-y-2">
        {(data.deadlines.projects.length + data.deadlines.tasks.length) === 0 ? (
          <EmptyState msg="No deadlines in the next fortnight." />
        ) : (
          <>
            {data.deadlines.projects.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <div className="text-sm font-medium">{p.title}</div>
                  <div className="text-xs text-muted-foreground">Project · {p.type}</div>
                </div>
                <Badge variant="secondary">{p.deadline}</Badge>
              </div>
            ))}
            {data.deadlines.tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <div className="text-sm font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">Task</div>
                </div>
                <Badge variant="secondary">{t.due_date}</Badge>
              </div>
            ))}
          </>
        )}
      </div>
    ),
  },
};

function MorningBrief({ data, settings, addQuickTask }) {
  const [added, setAdded] = useState([]);
  const [busy, setBusy] = useState(null);
  const suggestions = settings?._today_suggestions || [];

  const add = async (s, i) => {
    setBusy(i);
    try { await addQuickTask(s); setAdded((a) => [...a, i]); }
    catch { toast.error("Could not add task"); }
    finally { setBusy(null); }
  };

  return (
    <>
      {data.ai_brief_generated_at && (
        <div className="text-[11px] text-muted-foreground mb-2">{new Date(data.ai_brief_generated_at).toLocaleString()}</div>
      )}
      {data.ai_brief ? (
        <p className="text-sm leading-relaxed">{data.ai_brief}</p>
      ) : (
        <div className="text-sm text-muted-foreground py-6 text-center">
          {"Press "}<b>Start My Day</b>{" to generate today's brief and suggestions."}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="brief-suggestions">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Suggestions <span className="normal-case tracking-normal">· click to add as a task for today</span>
          </div>
          {suggestions.map((s, i) => {
            const isAdded = added.includes(i);
            return (
              <button
                key={s}
                disabled={isAdded || busy === i}
                onClick={() => add(s, i)}
                data-testid={`brief-suggestion-${i}`}
                className={cn(
                  "w-full text-left text-sm flex items-start gap-2 p-2 rounded-md transition-colors",
                  isAdded ? "opacity-60" : "hover:bg-accent"
                )}
              >
                {isAdded
                  ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-[hsl(var(--halo-teal))] shrink-0" />
                  : <Plus className="w-3.5 h-3.5 mt-0.5 text-[hsl(var(--studio-primary))] shrink-0" />}
                <span className={cn(isAdded && "line-through")}>{s}</span>
              </button>
            );
          })}
        </div>
      )}
      {settings?._today_brief_headlines?.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="brief-industry-pulse">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> Industry pulse
          </div>
          {settings._today_brief_headlines.map((h) => (
            <a
              key={h.id}
              href={h.url || "#"}
              target={h.url ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`brief-headline-${h.id}`}
              className="block text-sm p-2 rounded-md hover:bg-accent group"
            >
              <div className="font-medium leading-snug group-hover:text-[hsl(var(--studio-primary))]">{h.headline}</div>
              {h.source && <div className="text-[11px] text-muted-foreground mt-0.5">{h.source}</div>}
            </a>
          ))}
          <Link to="/news" className="inline-block pt-1 text-xs text-[hsl(var(--studio-primary))] hover:underline">
            Open News →
          </Link>
        </div>
      )}
    </>
  );
}

const PHASE_LABEL = {
  dawn: "Dawn",
  morning: "This morning",
  midday: "Midday",
  afternoon: "This afternoon",
  dusk: "Dusk",
  night: "Tonight",
};

function phaseFor(d) {
  const h = d.getHours();
  if (h < 5) return "night";
  if (h < 8) return "dawn";
  if (h < 12) return "morning";
  if (h < 15) return "midday";
  if (h < 17) return "afternoon";
  if (h < 20) return "dusk";
  return "night";
}

function greetFor(d) {
  const h = d.getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}

const Row = ({ label, value, tone }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={
      tone === "danger" ? "font-semibold text-[hsl(var(--studio-danger))]" :
      tone === "success" ? "font-semibold text-[hsl(var(--studio-success))]" :
      "font-semibold"
    }>{value}</span>
  </div>
);

const EmptyState = ({ msg }) => (
  <div className="text-sm text-muted-foreground py-6 text-center">{msg}</div>
);

function DayPlan({ events, tasks, emptyMsg, testid }) {
  const blocks = [
    ...events.map((e) => ({ id: e.id, kind: "event", title: e.title, start: e.start, end: e.end, color: e.color || "blue", location: e.location })),
    ...tasks.filter((t) => t.scheduled_for).map((t) => ({
      id: t.id,
      kind: "task",
      title: t.title,
      start: t.scheduled_for,
      end: new Date(new Date(t.scheduled_for).getTime() + (t.duration_minutes || 30) * 60000).toISOString(),
      color: "purple",
      done: t.status === "done",
    })),
  ].sort((a, b) => new Date(a.start) - new Date(b.start));

  if (blocks.length === 0) return <EmptyState msg={emptyMsg} />;

  return (
    <div className="space-y-2" data-testid={testid}>
      {blocks.map((b) => (
        <div key={`${b.kind}-${b.id}`} className="studio-schedule-block" data-color={b.color} data-testid={`${testid}-block-${b.id}`}>
          <div className="flex justify-between gap-3">
            <div className={cn("font-medium min-w-0 truncate", b.done && "line-through opacity-60")}>
              {b.kind === "task" ? "Task · " : ""}{b.title}
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
              {timeShort(b.start)} — {timeShort(b.end)}
            </div>
          </div>
          {b.location && <div className="text-xs text-muted-foreground mt-0.5">{b.location}</div>}
        </div>
      ))}
    </div>
  );
}

export const PriorityBadge = ({ priority }) => (
  <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-md studio-priority-${priority || "normal"}`}>
    {priority || "normal"}
  </span>
);
